<template>
	<v-form ref="complaintInformation">
		<h3><translate>Description of your complaint</translate></h3>
		<v-row v-if="policies">
			<h4><strong><translate>Policy number</translate></strong></h4>
			<v-radio-group
				v-model:model-value="complaintDescriptionData.selectedPolicy"
				:rules="[i => !!i || 'Please select a policy']"
				required
			>
				<v-radio
					v-for="policy in policies"
					:key="createPolicyLabel(policy)"
					:value="policy"
					color="primary"
					:label="createPolicyLabel(policy)"
				/>
			</v-radio-group>
		</v-row>
		<v-row v-else>
			<v-col>
				<v-text-field
					v-model:model-value="complaintDescriptionData.policyNumber"
					color="primary"
					name="policyNumber"
					:label="$gettext('Policy number')"
					variant="outlined"
				/>
			</v-col>
			<v-col>
				<v-text-field
					v-model:model-value="complaintDescriptionData.device"
					color="primary"
					name="policyDevice"
					:label="$gettext('Device')"
					variant="outlined"
				/>
			</v-col>
		</v-row>
		<v-row no-gutters>
			<v-col cols="12">
				<h4><strong><translate>What is your complaint about?</translate>*</strong></h4>
				<v-select
					v-model:model-value="complaintDescriptionData.complaintReason"
					:items="complaintOptions"
					item-title="text"
					item-value="value"
					name="subject"
					color="primary"
					:label="$gettext('Choose a complaint')"
					:rules="[i => !!i || $gettext('Please choose one of the available options')]"
					required
					variant="outlined"
				>
					<template #message="{ message }">
						{{ $gettext(message) }}
					</template>
				</v-select>
			</v-col>
			<v-col
				v-if="complaintDescriptionData.complaintReason === rejectionClaimReason"
			>
				<translate>
					If you disagree with the rejection of your claim, you can use the complaints procedure.
					Click
				</translate> <a :href="objectionsRoute"><translate>here</translate></a> <translate>
					to go to the objection form.
				</translate>
			</v-col>
			<v-col
				v-if="complaintDescriptionData.complaintReason === employeeReason"
			>
				<h5><strong><translate>Which employee does this concern?</translate></strong></h5>
				<v-radio-group
					v-model:model-value="complaintDescriptionData.employeeType"
					:rules="[i => !!i || $gettext('Please select which employee this concerns')]"
					required
				>
					<v-radio
						color="primary"
						:label="$gettext(storeEmployee)"
						:value="storeEmployee"
					/>
					<v-radio
						color="primary"
						:label="$gettext(customerServiceEmployee)"
						:value="customerServiceEmployee"
					/>
				</v-radio-group>
			</v-col>
			<v-col
				v-if="complaintDescriptionData.complaintReason === employeeReason && complaintDescriptionData.employeeType"
				cols="12"
			>
				<strong><translate>Do you know the name of the employee that you want to file a complaint about? If so, enter the name below</translate></strong>
				<v-textarea
					v-if="complaintDescriptionData.employeeType"
					v-model:model-value="complaintDescriptionData.employeeName"
					:label="$gettext('Enter the name of the employee here')"
					auto-grow
					color="primary"
					rows="1"
					variant="outlined"
				/>
				<strong v-if="complaintDescriptionData.employeeType === storeEmployee"><translate>Which store?</translate></strong>
				<v-textarea
					v-if="complaintDescriptionData.employeeType === storeEmployee"
					v-model:model-value="complaintDescriptionData.storeName"
					:label="$gettext('Enter the name of the store here (e.g. Store Amsterdam)')"
					auto-grow
					color="primary"
					rows="1"
					variant="outlined"
					:rules="[i => !!i || $gettext('Please fill in the name of the store you visited')]"
					required
				>
					<template #message="{ message }">
						{{ $gettext(message) }}
					</template>
				</v-textarea>
			</v-col>
			<v-col
				v-if="complaintDescriptionData.complaintReason === invoiceReason"
				cols="12"
			>
				<strong><translate>What kind of costs?</translate></strong>
				<v-radio-group
					v-model:model-value="complaintDescriptionData.invoiceCostType"
					:rules="[i => !!i || $gettext('Please select what kind of cost')]"
					required
				>
					<v-radio
						color="primary"
						:label="$gettext(invoiceDeductibleType)"
						:value="$gettext(invoiceDeductibleType)"
					/>
					<v-radio
						color="primary"
						:label="$gettext(invoicePremiumType)"
						:value="$gettext(invoicePremiumType)"
					/>
					<v-radio
						color="primary"
						:label="$gettext(invoiceOtherType)"
						:value="$gettext(invoiceOtherType)"
					/>
				</v-radio-group>
			</v-col>
			<v-col
				v-if="complaintDescriptionData.complaintReason === otherReason"
			>
				<strong><translate>What is your complaint about?</translate></strong>
				<v-textarea
					v-model:model-value="complaintDescriptionData.otherReasonText"
					:label="$gettext('Enter your complaint here')"
					auto-grow
					color="primary"
					rows="1"
					variant="outlined"
					:rules="[i => !!i || $gettext('Please fill in your complaint')]"
					required
				>
					<template #message="{ message }">
						{{ $gettext(message) }}
					</template>
				</v-textarea>
			</v-col>
			<v-col cols="12">
				<h4><strong><translate>Give a clear and complete description of the situation that led to your complaint below.</translate>*</strong></h4>
				<v-textarea
					v-model:model-value="complaintDescriptionData.complaintDescription"
					auto-grow
					color="primary"
					:label="$gettext('Enter your description here')"
					:rules="[i => !!i || $gettext('Please fill in the description of your complaint')]"
					required
					rows="6"
					variant="outlined"
				>
					<template #message="{ message }">
						{{ $gettext(message) }}
					</template>
				</v-textarea>
			</v-col>
			<v-col cols="12">
				<h4><strong><translate>Were you offered a solution for your complaint?</translate></strong></h4>
				<v-radio-group
					v-model:model-value="complaintDescriptionData.complaintOfferedSolution"
					:rules="[i => !!i || $gettext('Please select whether or not you\'ve been offered a solution')]"
					required
				>
					<v-radio
						color="primary"
						:label="$gettext(offeredSolution)"
						:value="offeredSolution"
					/>
					<v-textarea
						v-if="complaintDescriptionData.complaintOfferedSolution === offeredSolution"
						v-model:model-value="complaintDescriptionData.offeredSolutionText"
						auto-grow
						color="primary"
						:label="$gettext('Enter the offered solution here')"
						rows="6"
						variant="outlined"
						required
					>
						<template #message="{ message }">
							{{ $gettext(message) }}
						</template>
					</v-textarea>

					<v-radio
						color="primary"
						:label="$gettext(noOfferedSolution)"
						:value="noOfferedSolution"
					/>
				</v-radio-group>
			</v-col>
			<v-col cols="12">
				<h4><strong><translate>What solution do you want for your complaint?</translate></strong></h4>
				<v-textarea
					v-model:model-value="complaintDescriptionData.complaintWantedSolution"
					auto-grow
					color="primary"
					:label="$gettext('Enter the solution you want here')"
					rows="6"
					variant="outlined"
					:rules="[i => !!i || $gettext('Please fill in your desired solution')]"
					required
				>
					<template #message="{ message }">
						{{ $gettext(message) }}
					</template>
				</v-textarea>
			</v-col>
			<v-col>
				<strong><translate>Include attachment (optional)</translate></strong>
				<file-input
					v-model:model-value="complaintDescriptionData.attachment"
					show-size
					color="primary"
					accept="image/*,.txt,.docx,.pdf"
					:label="$gettext('Upload a file')"
					variant="outlined"
				/>
				<br>
			</v-col>
		</v-row>
	</v-form>
</template>

<script>

export default {
	name: 'ComplaintDescription',
	props: {
		policies: {
			type: Array,
			required: false,
			default: null,
		},

		modelValue: {
			type: Object,
			default: () => null,
		}
	},

	emits: ['update:modelValue',],

	data () {
		return {
			descriptionData: {
			},
			objectionsRoute: '/dashboard/objections',

			// watched information
			complaintDescriptionData: {
				employeeType: null,
				employeeName: '',
				storeName: '',
				otherReasonText: '',
				invoiceCostType: null,
				complaintReason: null,
				selectedPolicy: null,
				policyNumber: '',
				device: '',
				complaintFullReason: [],
				complaintDescription: '',
				complaintOfferedSolution: null,
				offeredSolutionText: '',
				complaintWantedSolution: '',
				attachment: null,
			},

			// complaint reasons
			rejectionClaimReason: 'A rejection of a claim',
			websiteReason: 'The website',
			processingReason: 'The processing time of my claim',
			policyReason: 'The policy of Tulip Assist',
			employeeReason: 'The behaviour of an employee',
			storeEmployee: 'Store employee',
			customerServiceEmployee: 'Customer service employee',
			invoiceReason: 'An invoice',
			otherReason: 'Other reason',
			invoiceDeductibleType: 'Deductible',
			invoicePremiumType: 'Premium',
			invoiceOtherType: 'Other costs',
			offeredSolution: 'Yes',
			noOfferedSolution: 'No',
		};
	},

	computed: {

		complaintOptions () {
			return [
				{
					text: this.$gettext('A rejection of a claim'),
					value: 'A rejection of a claim'
				},
				{
					text: this.$gettext('The website'),
					value: 'The website',
				},
				{
					text: this.$gettext('The processing time of my claim'),
					value: 'The processing time of my claim'
				},
				{
					text: this.$gettext('The policy of Tulip Assist'),
					value: 'The policy of Tulip Assist'
				},
				{
					text: this.$gettext('The behaviour of an employee'),
					value: 'The behaviour of an employee'
				},
				{
					text: this.$gettext('An invoice'),
					value: 'An invoice'
				},
				{
					text: this.$gettext('Other reason'),
					value: 'Other reason'
				},
			];
		},

		/**
		 * @returns {string}
		 */
		getSelectedPhone: function () {
			return (this.selectedPolicy !== null && ('phoneName' in this.selectedPolicy)) ? this.selectedPolicy.phoneName : '';
		},

		/**
		 * @returns {string}
		 */
		getSelectedPolicyNumber: function () {
			return (this.selectedPolicy !== null && ('policyNumber' in this.selectedPolicy)) ? this.selectedPolicy.policyNumber : '';
		},
	},

	watch: {
		complaintDescriptionData: {
			/**
			 * Emit the complaint data when a change is detected
			 */
			handler () {
				this.getSendData();
				this.$emit('update:modelValue', this.descriptionData);
			},
			deep: true
		},
	},

	methods: {
		/**
		 * Checks whether all required fields are filled in
		 *
		 * @returns {boolean}
		 */
		async validate () {
			const {
				valid
			} = await this.$refs.complaintInformation.validate();

			return valid;
		},

		/**
		 * @param {object} policy
		 * @returns {string}
		 */
		createPolicyLabel (policy) {
			if(policy.phoneName) {
				return policy.policyNumber.concat(' - ', policy.phoneName);
			}
			return policy.policyNumber;
		},

		/**
		 * Sort the reason data into an array and return it
		 *
		 * @returns {Array}
		 */
		getCompleteReason () {
			const completeReason = [];

			if (this.complaintDescriptionData.complaintReason) {
				completeReason.push(this.$gettext(this.complaintDescriptionData.complaintReason));
				if (this.complaintDescriptionData.complaintReason === this.employeeReason && this.complaintDescriptionData.employeeType) {
					completeReason.push(this.$gettext(this.complaintDescriptionData.employeeType));
					if (this.complaintDescriptionData.employeeName !== '') {
						completeReason.push(this.$gettext(this.complaintDescriptionData.employeeName));
					}
					if (this.complaintDescriptionData.employeeType === this.storeEmployee) {
						completeReason.push(this.$gettext(this.complaintDescriptionData.storeName));
					}
				}
				if (this.complaintDescriptionData.complaintReason === this.invoiceReason && this.complaintDescriptionData.invoiceCostType) {
					completeReason.push(this.$gettext(this.complaintDescriptionData.invoiceCostType));
				}
				if (this.complaintDescriptionData.complaintReason === this.otherReason) {
					completeReason.push(this.$gettext(this.complaintDescriptionData.otherReasonText));
				}
			}
			return completeReason;
		},

		/**
		 * Retrieve and format the data for the form
		 */
		getSendData () {
			if (this.policies) {
				this.descriptionData.policyNumber = this.getSelectedPolicyNumber;
				this.descriptionData.device = this.getSelectedPhone;
			} else {
				this.descriptionData.policyNumber = this.complaintDescriptionData.policyNumber;
				this.descriptionData.device = this.complaintDescriptionData.device;
			}
			this.descriptionData.complaintFullReason = this.getCompleteReason();
			this.descriptionData.complaintDescription = this.complaintDescriptionData.complaintDescription;
			this.descriptionData.offeredSolutionText = this.complaintDescriptionData.offeredSolutionText;
			this.descriptionData.complaintWantedSolution = this.complaintDescriptionData.complaintWantedSolution;
			this.descriptionData.attachment = this.complaintDescriptionData.attachment;
		},
	},
};
</script>

